import React, { useState } from 'react';
import { XMarkIcon, TicketIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useReduxStore } from '../hooks/useReduxStore';
import APIRequest from '../services/APIRequest';
import { toast } from 'react-toastify';

export default function SupportTicketModal({ isOpen, onClose }) {
  const { getState } = useReduxStore();
  const { userData } = getState("AUTH");
  
  const [formData, setFormData] = useState({
    subject: '',
    description: '',
    attachments: []
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setFormData(prev => ({
      ...prev,
      attachments: [...prev.attachments, ...files]
    }));
  };

  const removeAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');
    
    try {
      // Create FormData for multipart/form-data
      const formDataToSend = new FormData();
      formDataToSend.append('subject', formData.subject);
      formDataToSend.append('description', formData.description);
      
      // Add attachments
      formData.attachments.forEach((file, index) => {
        formDataToSend.append(`attachments`, file);
      });
       
      const response = await APIRequest.post('user/tickets', formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.ok && response.data.success) {
        setSubmitMessage('success');
        toast.success('Support ticket created successfully!');
        
        // Reset form and close modal after success
        setTimeout(() => {
          onClose();
          setFormData({
            subject: '',
            description: '',
            attachments: []
          });
          setSubmitMessage('');
        }, 2000);
      } else {
        setSubmitMessage('error');
        toast.error(response.data?.message || 'Failed to create support ticket');
      }
    } catch (error) {
      console.error('Support ticket creation error:', error);
      setSubmitMessage('error');
      toast.error('Failed to create support ticket. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };


  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl bg-blueGray border border-borderGray rounded-lg shadow-xl p-6 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center space-x-3">
            <TicketIcon className="h-6 w-6 text-primaryBlue" />
            <h2 className="text-xl font-semibold text-white">Create Support Ticket</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors p-1 rounded-full hover:bg-gray-700"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Success/Error Message */}
        {submitMessage && (
          <div className={`mb-6 p-4 rounded-lg ${
            submitMessage === 'success' 
              ? 'bg-green-900/20 border border-green-500 text-green-400' 
              : 'bg-red-900/20 border border-red-500 text-red-400'
          }`}>
            {submitMessage === 'success' 
              ? 'Ticket created successfully! Our support team will get back to you soon.' 
              : 'Failed to create ticket. Please try again.'}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* User Info */}
          {/* <div className="bg-[#384355] p-4 rounded-lg border border-borderGray">
            <h3 className="text-lg font-semibold text-white mb-3">Contact Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-400">Name:</span>
                <span className="text-white ml-2">{userData?.name || 'N/A'}</span>
              </div>
              <div>
                <span className="text-gray-400">Email:</span>
                <span className="text-white ml-2">{userData?.email || 'N/A'}</span>
              </div>
            </div>
          </div> */}

          {/* Subject */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Subject <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              name="subject"
              value={formData.subject}
              onChange={handleInputChange}
              placeholder="Brief description of your issue"
              className="w-full p-3 bg-[#384355] border border-borderGray text-white rounded-lg focus:ring-2 focus:ring-primaryBlue focus:border-transparent placeholder-gray-400"
              required
            />
          </div>

          {/* Priority and Category */}
          {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Priority <span className="text-red-400">*</span>
              </label>
              <select
                name="priority"
                value={formData.priority}
                onChange={handleInputChange}
                className="w-full p-3 bg-[#384355] border border-borderGray text-white rounded-lg focus:ring-2 focus:ring-primaryBlue focus:border-transparent"
                required
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Category <span className="text-red-400">*</span>
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                className="w-full p-3 bg-[#384355] border border-borderGray text-white rounded-lg focus:ring-2 focus:ring-primaryBlue focus:border-transparent"
                required
              >
                <option value="technical">Technical Issue</option>
                <option value="billing">Billing Question</option>
                <option value="feature">Feature Request</option>
                <option value="bug">Bug Report</option>
                <option value="general">General Inquiry</option>
              </select>
            </div>
          </div> */}

          {/* Priority Indicator */}
          {/* <div className="flex items-center space-x-2">
            <ExclamationTriangleIcon className="h-5 w-5 text-gray-400" />
            <span className="text-sm text-gray-300">Priority:</span>
            <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getPriorityColor(formData.priority)}`}>
              {formData.priority.charAt(0).toUpperCase() + formData.priority.slice(1)}
            </span>
          </div> */}

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Description <span className="text-red-400">*</span>
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Please provide detailed information about your issue..."
              rows={6}
              className="w-full p-3 bg-[#384355] border border-borderGray text-white rounded-lg focus:ring-2 focus:ring-primaryBlue focus:border-transparent placeholder-gray-400 resize-none"
              required
            />
          </div>

          {/* File Attachments */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Attachments (Optional)
            </label>
            <div className="border-2 border-dashed border-borderGray rounded-lg p-4">
              <input
                type="file"
                multiple
                onChange={handleFileChange}
                className="hidden"
                id="file-upload"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
              />
              <label
                htmlFor="file-upload"
                className="cursor-pointer flex flex-col items-center justify-center py-4"
              >
                <TicketIcon className="h-8 w-8 text-gray-400 mb-2" />
                <span className="text-sm text-gray-300">Click to upload files</span>
                <span className="text-xs text-gray-500 mt-1">PDF, DOC, Images (Max 10MB each)</span>
              </label>
            </div>
            
            {/* File List */}
            {formData.attachments.length > 0 && (
              <div className="mt-3 space-y-2">
                {formData.attachments.map((file, index) => (
                  <div key={index} className="flex items-center justify-between bg-[#384355] p-2 rounded border border-borderGray">
                    <span className="text-sm text-white truncate">{file.name}</span>
                    <button
                      type="button"
                      onClick={() => removeAttachment(index)}
                      className="text-red-400 hover:text-red-300 text-sm"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Submit Button */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-borderGray text-gray-300 rounded-lg hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-2 bg-primaryBlue hover:bg-primaryBlue/90 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Creating Ticket...' : 'Create Ticket'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
